import type { ReactNode } from "react";

import { EmptyState } from "@solarkey/ui";

import { PageHeader } from "../shell/PageHeader";

/* ════════════════════════════════════════════════════════════════════════════
   M1 — Organisations and relationships (wireframe §05, Admin).

   The screen that WILL show organisation types and grants of scope / role bundle
   / lens / authority / validity, with expiry enforced.

   It shows NOTHING yet, on purpose. The tables exist in the catalog
   (`governance.organisation`, `relationship`, `relationship_lens`,
   `authority_envelope` + `authority_limit`, `asset_group`) but no endpoint serves
   them, so this screen makes no network call and has no data of its own.

   It previously rendered the wireframe's illustrative demo set as if it were this
   tenant's register — including invented organisation and person names, which
   CLAUDE.md rule 5 forbids outright (site identifiers only). Those are gone: an
   empty register that says why is correct; a populated one that is make-believe
   is not. The grant editor (New relationship / Save / Revoke) arrives with the
   endpoint that can persist it, not before.
   ════════════════════════════════════════════════════════════════════════════ */

/** What the screen needs before it can render a register, in the order it needs it. */
const PENDING: { what: string; source: string }[] = [
  {
    what: "Organisations and their types",
    source: "GET /v1/organisations — reads governance.organisation",
  },
  {
    what: "Relationships per organisation, with validity and expiry",
    source: "GET /v1/organisations/{id}/relationships — reads governance.relationship",
  },
  {
    what: "The lens set granted on a relationship",
    source: "relationship_lens rows on the relationship above",
  },
  {
    what: "Authority envelope and its currency-denominated limits",
    source: "authority_envelope + authority_limit on the relationship above",
  },
  {
    what: "Asset scope of a grant",
    source: "asset_group (and its site membership)",
  },
  {
    what: "People per organisation",
    source: "no identity-to-organisation link is served yet",
  },
];

export function Organisations(): ReactNode {
  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <PageHeader
        title="Organisations and access"
        scope="Not wired yet — no organisations endpoint in this build"
        family="admin"
        showTimeControl={false}
      />

      <EmptyState
        variant="no_data"
        title="The organisation register is not served yet"
        message={
          "Organisations, relationships, lens grants and authority envelopes are " +
          "recorded in the platform catalog, but no API endpoint reads them, so this " +
          "screen has nothing to show. It will not display an example register in " +
          "their place."
        }
      />

      <div className="card">
        <h4>What this screen is waiting on</h4>
        <table className="table" aria-label="Organisations screen data dependencies">
          <thead>
            <tr>
              <th>Needed</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {PENDING.map((p) => (
              <tr key={p.what}>
                <td>{p.what}</td>
                <td className="text-text-muted">{p.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ marginTop: 8, fontSize: 11, color: "var(--sk-color-ink-3)", lineHeight: 1.4 }}>
          A person belongs to one organisation. Contractors are granted as
          organisations, never created as users inside the tenant — that rule is
          what the register will enforce once it has one.
        </p>
      </div>
    </div>
  );
}
